/* eslint-disable no-underscore-dangle */
/* eslint-disable max-lines */
import { useContext, useEffect, useRef, useState } from 'react';
import { Button, Grid, Typography, Box, LinearProgress, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/system';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { Buffer } from 'buffer';
import ErrorIcon from '../../images/ErrorX.svg';
import ErrorTriangleIcon from '../../images/ErrorTriangle.svg';
import DoneTick from '../../images/Done.svg';
import DoneTickGreen from '../../images/Done-green.svg';
import NewAddressImage from '../../images/Upload_GIF_1.gif';
import NewAddressSpanish from '../../images/Upload_GIF_1ES.gif';
import { SidebarStyles as classes } from './SidebarStyles';
import LeadGenerationContext from '../../context/LeadGenerationContext';
import cloudUpload from '../../images/cloud-upload.svg';
import uploadBtnIcon from '../../images/upload_btn_icon.svg';
import cameraBtnIcon from '../../images/camera_upload_icon.svg';
import CustomTooltip from '../Tooltip/CustomTooltip';
import Loader from '../Loader/Loader';
import {
  ADDRESSLINE1_MAX_ERR,
  ADDRESSLINE2_MAX_ERR,
  DONE,
  FAILED,
  HOME_PR,
  HOME_VI,
  MANDATORY_FIELD_ERR,
  NAME_MISMATCH_ERROR,
  ORIENTAL_ORANGE,
  UPLOAD_ADDRESS_GIF_TEXT,
} from '../../constants';
import { LayoutStyles as bundleClasses } from '../Bundle/LayoutStyles';
import {
  generateFileMD5,
  generateFileMD5Cordova,
  uploadDocument,
  uploadDocumentByPreSignedURL,
  uploadDocumentXL,
  wcgenerateFileMD5,
} from '../../services/uploadService';
import { commonUtility, commonUtilityValueReturn } from '../LeadGeneration/UtilityFunctions';
import { getOcrAddressValidation } from '../../services/formService';
import { productMapping } from '../../services/productMapping';
import { productNameURLMapping } from '../../services/productNameURLMapping';
import { businessMapping } from '../../services/businessMapping';
import { dgiMapping } from '../../services/dgiMapping';
import {
  clearUploadFocus,
  commonTernary,
  getCordovaPlatformId,
  getRoot,
  getRuntimePlatform,
  isBundle,
  isCDIraproduct,
  isCordovaFn,
  isIRAEducativaProduct,
  isWebComponentFlow,
  resolveFileToBlob,
} from '../commonCode';
import { DeleteIcon, FileIcon } from '../../images/svg/CustomizedSVG';
import { handleApiEnviroment } from '../../WebComponents/commonComponents/customUtils';

// Added `isIOSCordova` param so we can pick the right upload strategy.
const getDataResponse = async (
  response,
  file,
  fileMd5Base64,
  fileType1,
  baseContext,
  props,
  isIOSCordova // ← NEW
) => {
  const { setCmAddressDocLoading } = baseContext;
  const { setUploadStatus, setUploadData, setUploadDataError } = props;

  if (response.data) {
    const documentId = commonUtility(response.data.documentId);
    const preSignedUrl = commonUtility(response.data.preSignedUrl);

    if (preSignedUrl && documentId) {
      let resp;

      try {
        console.log('uploadDocumentByPreSignedURL');
        // ─── Android / Web path (unchanged) ─────────────────────────────
        resp = await uploadDocumentByPreSignedURL(
          preSignedUrl,
          file,
          fileMd5Base64,
          baseContext.setTaxUploadProgress,
          baseContext.setTaxLinearProgress,
          fileType1,
          { commonDocUpload: true }
        );
      } catch (err) {
        console.log('[CommonDocumentUpload] Pre-signed URL upload threw an exception', err);
        setCmAddressDocLoading((prev) => ({ ...prev, [fileType1]: false }));
        setUploadStatus(FAILED);
        return;
      }

      if (resp && resp.status === 200) {
        setUploadData((prevState) => ({
          ...prevState,
          document: documentId,
        }));
        setUploadDataError((prevState) => ({
          ...prevState,
          document: '',
        }));
        setCmAddressDocLoading((prev) => ({ ...prev, [fileType1]: false }));
        setUploadStatus(DONE);
      } else {
        setCmAddressDocLoading((prev) => ({ ...prev, [fileType1]: false }));
        setUploadStatus(FAILED);
      }
    }
  }
};

// Threads `isIOSCordova` through to getDataResponse.
const documentResponse = async (...args) => {
  const fileMD5 = args[0];
  const fileExtension = args[1];
  const fileName = args[2];
  const fileType1 = args[3];
  const fileSize = args[4];
  const file = args[5];
  const baseContext = args[6];
  const props = args[7];
  const isIOSCordova = args[8]; // ← NEW

  console.log({ fileMD5, fileExtension, fileName, fileType1, fileSize, file });

  const { setCmAddressDocLoading } = baseContext;
  const { setUploadStatus } = props;

  if (fileMD5) {
    const fileMd5Base64 = Buffer.from(fileMD5, 'hex').toString('base64');

    const response = await uploadDocumentXL(
      fileExtension,
      fileName,
      fileType1,
      fileSize,
      fileMd5Base64,
      baseContext.accessToken,
      baseContext.setTaxUploadProgress,
      baseContext.setTaxLinearProgress,
      baseContext.initiateUiResponse,
      {
        isWCFlow: baseContext?.wcFlow,
        env: handleApiEnviroment(baseContext?.environment),
        commonDocUpload: true,
      }
    );

    if (response?.status === 201) {
      await getDataResponse(
        response,
        file,
        fileMd5Base64,
        fileType1,
        baseContext,
        props,
        isIOSCordova // ← NEW
      );
    } else {
      setCmAddressDocLoading((prev) => ({ ...prev, [fileType1]: false }));
      setUploadStatus(FAILED);
    }
  } else {
    setCmAddressDocLoading((prev) => ({ ...prev, [fileType1]: false }));
    setUploadStatus(FAILED);
  }
};
/**
 * FIX: ERR_UPLOAD_FILE_CHANGED on Android Google Drive uploads
 *
 * ROOT CAUSE:
 * Google Drive (and other cloud provider) files on Android are backed by a
 * content:// URI that becomes stale after the first read. When the File object
 * is passed directly to FormData or to uploadDocumentByPreSignedURL, Chrome's
 * network layer tries to re-read the URI during the actual HTTP transfer — by
 * that point the URI has already been consumed/expired → ERR_UPLOAD_FILE_CHANGED.
 *
 * THE FIX:
 * Eagerly materialize the file into an in-memory Blob immediately upon selection,
 * before ANY async upload operation. Every subsequent read (MD5, FormData, PUT)
 * operates on a stable, memory-backed Blob that cannot go stale.
 *
 * DEPLOYMENT NOTE:
 * Only `commonUploadFile` in CommonDocumentUpload.jsx needs to change.
 * No API, no service, no other component is touched.
 */

/**
 * Reads any File/Blob/URI into a stable in-memory Blob.
 * - If already a Blob with size > 0: returns it directly (zero-copy, no overhead).
 * - Otherwise: uses FileReader to pull bytes into an ArrayBuffer and wraps it.
 *
 * This is the ONLY place we need the platform-aware resolveFileToBlob logic.
 * After this function resolves, the returned Blob is 100% memory-backed and
 * safe to read multiple times without hitting any content-provider URI.
 *
 * @param {File|Blob|string} file
 * @param {string} mimeType  - used when constructing the final Blob
 * @returns {Promise<Blob>}
 */
const materializeFileToStableBlob = (file, mimeType) =>
  new Promise((resolve, reject) => {
    // iOS fast path: bytes already read synchronously in fileInputHandler
    // before any await — security-scoped URL was still valid at that point.
    console.log({ filepath_iosBuffer: file.__iosArrayBuffer });
    console.log('mime', mimeType);
    console.log('file', file);
    if (file.__iosArrayBuffer) {
      try {
        resolve(new Blob([file.__iosArrayBuffer], { type: mimeType || file.type }));
      } catch (e) {
        reject(e);
      }
      return;
    }

    // Android / Web: standard FileReader copy.
    // For Android this detaches bytes from the content:// URI.
    // For Web the File is already memory-backed — this is a safe copy.
    if (file instanceof Blob && file.size > 0) {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          resolve(new Blob([reader.result], { type: mimeType || file.type }));
        } catch (e) {
          reject(e);
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
      return;
    }

    // Cordova file:// string URI fallback (camera plugin etc.)
    resolveFileToBlob(file)
      .then((blob) => materializeFileToStableBlob(blob, mimeType))
      .catch(reject);
  });

/**
 * DROP-IN REPLACEMENT for commonUploadFile in CommonDocumentUpload.jsx
 *
 * The ONLY change vs. the original:
 *   1. After basic size/extension validation, call materializeFileToStableBlob().
 *   2. Use the returned `stableBlob` everywhere the original used `file` for I/O.
 *   3. Keep `file.name`, `file.size`, `file.type` from the original File object
 *      for metadata (these are just properties, not I/O reads, so they're safe).
 *
 * Everything else — error messages, state setters, service calls, progress tracking
 * — is byte-for-byte identical to the original so there is zero regression risk.
 */
// export const commonUploadFile = async (file, baseContext, props) => {
//   const { fileType, setUploadStatus, setUploadData, setUploadDataError, setUploadFileData } = props;
//   const { setCmAddressDocLoading } = baseContext;

//   // Detect platform — same pattern as saveFileInCordova()
//   const platformId = getCordovaPlatformId(); // 'android' | 'ios' | undefined

//   setCmAddressDocLoading((prev) => ({ ...prev, [fileType]: true }));
//   setUploadFileData((prev) => ({
//     ...prev,
//     name: file.name,
//     size: file.size,
//     type: file.type,
//   }));

//   const fileName = file.name;
//   const fileParts = fileName.split('.');
//   const fileExtension = fileParts[fileParts.length - 1].toLowerCase();

//   const fileSize = file.size;

//   if (fileSize > 10485760) {
//     setCmAddressDocLoading((prev) => ({ ...prev, [fileType]: false }));
//     setUploadData((prevState) => ({ ...prevState, ocrErrorMessage: '' }));
//     setUploadDataError((prevState) => ({
//       ...prevState,
//       ocrUIErrors: `The file exceeds the maximum upload size (10 Mb).`,
//     }));
//     setUploadStatus(FAILED);
//     return;
//   }

//   const extensions = ['pdf', 'jpg', 'jpeg', 'png'];
//   if (!extensions.includes(fileExtension)) {
//     setCmAddressDocLoading((prev) => ({ ...prev, [fileType]: false }));
//     setUploadStatus(FAILED);
//     setUploadDataError((prevState) => ({
//       ...prevState,
//       ocrUIErrors: 'Only files with the following formats are allowed: PNG, JPG, PDF.',
//     }));
//     return;
//   }

//   // ── THE FIX: eagerly materialize BEFORE any upload logic ───────────────────
//   // This is the single change. We convert the File (which may be backed by a
//   // transient content:// URI on Android) into a plain in-memory Blob right now,
//   // while the gesture/selection is still fresh and the URI is still valid.
//   // All code below uses `stableFile` instead of `file` for actual I/O.
//   let stableFile;
//   try {
//     stableFile = await materializeFileToStableBlob(file, file.type);
//     console.info(
//       `[CommonDocumentUpload] File materialized to memory: ${fileName} (${stableFile.size} bytes)`
//     );
//   } catch (err) {
//     console.error('[CommonDocumentUpload] Failed to materialize file to memory', err);
//     setCmAddressDocLoading((prev) => ({ ...prev, [fileType]: false }));
//     setUploadStatus(FAILED);
//     setUploadDataError((prev) => ({
//       ...prev,
//       ocrUIErrors: 'Unable to process the file. Please try again.',
//     }));
//     return;
//   }
//   // ── END OF FIX ─────────────────────────────────────────────────────────────

//   if (fileSize > 3145728) {
//     // >3 MB path: XL upload + pre-signed PUT
//     // stableFile is already a Blob — no need for resolveFileToBlob again.
//     try {
//       const platform = getRuntimePlatform();
//       console.info(`[CommonDocumentUpload] >3MB upload on ${platform}`);

//       // generateFileMD5Cordova now reads stableFile (in-memory Blob, always safe)
//       const fileMD5 = await generateFileMD5Cordova(stableFile);

//       await documentResponse(
//         fileMD5,
//         fileExtension,
//         fileName,
//         fileType,
//         fileSize,
//         stableFile, // ← was `safeFile` (result of resolveFileToBlob); now stableFile
//         baseContext,
//         props
//       );
//     } catch (err) {
//       console.error(`[CommonDocumentUpload] >3MB upload failed on ${getRuntimePlatform()}`, err);
//       setCmAddressDocLoading((prev) => ({ ...prev, [fileType]: false }));
//       setUploadStatus(FAILED);
//       setUploadDataError((prev) => ({
//         ...prev,
//         ocrUIErrors: 'Unable to process the file. Please try again.',
//       }));
//     }
//   } else {
//     // ≤3 MB path: direct multipart POST
//     const formData = new FormData();
//     // Append stableFile (in-memory Blob) with the original fileName so the
//     // server still receives the correct filename. FormData.append(key, blob, filename)
//     // is universally supported.
//     formData.append('file', stableFile, fileName); // ← was `file`; now stableFile + explicit name

//     const response = await uploadDocument(
//       fileExtension,
//       fileName,
//       fileType,
//       formData,
//       baseContext.accessToken,
//       baseContext.setTaxUploadProgress,
//       baseContext.setTaxLinearProgress,
//       {
//         isWCFlow: baseContext?.wcFlow,
//         env: handleApiEnviroment(baseContext?.environment),
//         commonDocUpload: true,
//       }
//     );

//     if (response?.data) {
//       setUploadData((prevState) => ({
//         ...prevState,
//         document: response.data.documentId,
//         ...(baseContext.currentPage === 'upload-documents-later' && {
//           [fileType]: response.data.documentId,
//         }),
//       }));
//       setUploadDataError((prevState) => ({
//         ...prevState,
//         document: '',
//         ...(baseContext.currentPage === 'upload-documents-later' && {
//           [fileType]: '',
//         }),
//       }));
//       setUploadStatus(DONE);
//       setCmAddressDocLoading((prev) => ({ ...prev, [fileType]: false }));
//     } else {
//       setCmAddressDocLoading((prev) => ({ ...prev, [fileType]: false }));
//       setUploadStatus(FAILED);
//     }
//   }
// };

export const commonUploadFile = async (file, baseContext, props) => {
  const { fileType, setUploadStatus, setUploadData, setUploadDataError, setUploadFileData } = props;
  const { setCmAddressDocLoading } = baseContext;

  const platformId = getCordovaPlatformId();
  const isCordovaFunc = isCordovaFn();

  console.log('isCordovaFn', isCordovaFunc);
  const isIOSCordova = platformId === 'ios';

  setCmAddressDocLoading((prev) => ({ ...prev, [fileType]: true }));
  setUploadFileData((prev) => ({
    ...prev,
    name: file.name,
    size: file.size,
    type: file.type,
  }));

  const fileName = file.name;
  const fileParts = fileName.split('.');
  const fileExtension = fileParts[fileParts.length - 1].toLowerCase();
  const fileSize = file.size;

  // ── Size guard (unchanged) ────────────────────────────────────────────────
  if (fileSize > 10485760) {
    setCmAddressDocLoading((prev) => ({ ...prev, [fileType]: false }));
    setUploadData((prevState) => ({ ...prevState, ocrErrorMessage: '' }));
    setUploadDataError((prevState) => ({
      ...prevState,
      ocrUIErrors: `The file exceeds the maximum upload size (10 Mb).`,
    }));
    setUploadStatus(FAILED);
    return;
  }

  // ── Extension guard (unchanged) ───────────────────────────────────────────
  const extensions = ['pdf', 'jpg', 'jpeg', 'png'];
  if (!extensions.includes(fileExtension)) {
    setCmAddressDocLoading((prev) => ({ ...prev, [fileType]: false }));
    setUploadStatus(FAILED);
    setUploadDataError((prevState) => ({
      ...prevState,
      ocrUIErrors: 'Only files with the following formats are allowed: PNG, JPG, PDF.',
    }));
    return;
  }

  // ── Materialize (unchanged) ───────────────────────────────────────────────
  let stableFile;
  if (isCordovaFunc) {
    try {
      stableFile = await materializeFileToStableBlob(file, file.type);
      console.log(
        `[CommonDocumentUpload] [${
          isCordovaFunc ? 'iOS' : platformId || 'web'
        }] materialized: ${fileName} (${stableFile.size} bytes)`
      );
    } catch (err) {
      console.error('[CommonDocumentUpload] materializeFileToStableBlob failed', err);
      setCmAddressDocLoading((prev) => ({ ...prev, [fileType]: false }));
      setUploadStatus(FAILED);
      setUploadDataError((prev) => ({
        ...prev,
        ocrUIErrors: '',
      }));
      return;
    }
  }
  const selectedFile = isCordovaFunc ? stableFile : file;

  // ── >3 MB path ────────────────────────────────────────────────────────────
  if (fileSize > 3145728) {
    try {
      console.log(`[CommonDocumentUpload] >3MB path on ${getRuntimePlatform()}`);

      const fileMD5 = isCordovaFunc
        ? await generateFileMD5Cordova(stableFile)
        : await generateFileMD5(file);
      console.log({ fileMD5 });
      await documentResponse(
        fileMD5,
        fileExtension,
        fileName,
        fileType,
        fileSize,
        selectedFile,
        baseContext,
        props,
        isIOSCordova // ← NEW: lets documentResponse → getDataResponse choose XHR path
      );
    } catch (err) {
      console.log(`[CommonDocumentUpload] >3MB upload failed on ${getRuntimePlatform()}`, err);
      setCmAddressDocLoading((prev) => ({ ...prev, [fileType]: false }));
      setUploadStatus(FAILED);
      setUploadDataError((prev) => ({
        ...prev,
        ocrUIErrors: '',
      }));
    }
  } else {
    // ── ≤3 MB path (unchanged) ───────────────────────────────────────────────
    const formData = new FormData();
    formData.append('file', selectedFile, fileName);

    console.log('formData', formData);

    const response = await uploadDocument(
      fileExtension,
      fileName,
      fileType,
      formData,
      baseContext.accessToken,
      baseContext.setTaxUploadProgress,
      baseContext.setTaxLinearProgress,
      baseContext.initiateUiResponse,
      {
        isWCFlow: baseContext?.wcFlow,
        env: handleApiEnviroment(baseContext?.environment),
        commonDocUpload: true,
      }
    );

    if (response?.data) {
      setUploadData((prevState) => ({
        ...prevState,
        document: response.data.documentId,
        ...(baseContext.currentPage === 'upload-documents-later' && {
          [fileType]: response.data.documentId,
        }),
      }));
      setUploadDataError((prevState) => ({
        ...prevState,
        document: '',
        ...(baseContext.currentPage === 'upload-documents-later' && {
          [fileType]: '',
        }),
      }));
      setUploadStatus(DONE);
      setCmAddressDocLoading((prev) => ({ ...prev, [fileType]: false }));
    } else {
      setCmAddressDocLoading((prev) => ({ ...prev, [fileType]: false }));
      setUploadStatus(FAILED);
    }
  }
};
function CommonDocumentUpload(props) {
  const { t } = useTranslation();
  const leadGenerationContext = useContext(LeadGenerationContext);
  const baseContext = isWebComponentFlow();
  const {
    productName,
    addressInformation,
    setAddressInformation,
    setIsOcrErrMessageFound,
    isOcrErrMessageFound,
    location,
    cmAddressDocLoading,
    isLightTheme,
    theme,
    setProductDataIRA,
    taxUploadProgress,
    setTaxUploadProgress,
    setTaxLinearProgress,
    setIsScreenFiveDirty,
  } = baseContext;
  const fileInput = useRef(null);
  const {
    vouchedAddrEdited,
    history,
    pagePath,
    isBundleFlag,
    handleChange,
    setUploadData,
    uploadStatus,
    uploadDocHeading,
    setUploadStatus,
    setUploadDataError,
    productDataIRAErrors,
    toolTipData,
    uploadError,
    uploadFileData,
    setUploadFileData,
    fileType,
    uploadData,
    columnLayout,
  } = props;
  const [checkFlow, setCheckFlow] = useState('');
  useEffect(() => {
    if (businessMapping(productName)) {
      setCheckFlow('commercial-form');
    } else {
      setCheckFlow('retail-form');
    }
  }, []);

  const themes = useTheme();
  const isMobile = useMediaQuery(themes?.breakpoints?.down(600), {
    defaultMatches: true,
  });

  const checkFileInput = () => {
    try {
      if (fileInput.current && fileInput.current.value) {
        fileInput.current.value = '';
      }
    } catch (err) {
      console.warn('File input reset failed:', err);
    }
  };
  const buttonClickHandler = (e) => {
    setIsScreenFiveDirty(true);
    // e.preventDefault() removed — Cordova Android requires gesture
    // trust chain to remain intact for file picker to open correctly
    setUploadData((prevState) => ({
      ...prevState,
      ocrErrMessage: '',
    }));
    setTimeout(() => {
      checkFileInput();
      fileInput.current?.click();
    }, 0);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      const file = files[0];
      setIsScreenFiveDirty(true);
      setUploadData((prevState) => ({
        ...prevState,
        ocrErrMessage: '',
        file,
      }));
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      if (fileInput.current) {
        const dt = new DataTransfer();
        dt.items.add(file);
        fileInput.current.files = dt.files;
      }
      await commonUploadFile(file, baseContext, {
        fileType,
        setUploadStatus,
        setUploadData,
        setUploadDataError,
        setUploadFileData,
      });
    }
  };

  const fileInputHandler = async (e) => {
    setIsScreenFiveDirty(true);
    e.preventDefault();
    const file = e.target.files[0];
    if (file === undefined || file === null) return;

    if (getCordovaPlatformId() === 'ios') {
      // CRITICAL: FileReader.readAsArrayBuffer(file) is called here with NO await
      // before it. This keeps it in the synchronous call stack of the onChange event.
      //
      // Why this matters: iOS revokes the security-scoped URL backing the File object
      // within milliseconds of the picker closing (triggered by DocumentManager's
      // thumbnail process crashing — confirmed by QLThumbnailErrorDomain Code=102 and
      // UIViewServiceErrorDomain Code=1 in the device logs). Any FileReader started
      // after the first await yields — including inside commonUploadFile — hits a
      // revoked URL and fires onerror, landing in the catch block.
      //
      // iOS guarantees: a read already IN PROGRESS at revocation time completes
      // successfully. So we start the read NOW and await the already-running promise.
      const iosEagerRead = new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(file); // synchronous — no await before this line
      });

      let arrayBuffer;
      try {
        arrayBuffer = await iosEagerRead;
      } catch (readErr) {
        console.error('[CommonDocumentUpload] [iOS] eager-read failed', readErr);
        setUploadStatus(FAILED);
        // setUploadDataError((prev) => ({
        //   ...prev,
        //   ocrUIErrors:
        //     'Unable to read the file. Please try selecting it again, or use the Camera option.',
        // }));
        return;
      }

      // Attach bytes to the File object — non-enumerable so nothing iterating
      // File properties is affected. materializeFileToStableBlob reads this
      // directly, skipping any further FileReader I/O on iOS.
      Object.defineProperty(file, '__iosArrayBuffer', {
        value: arrayBuffer,
        writable: false,
        enumerable: false,
        configurable: false,
      });

      console.log(
        `[CommonDocumentUpload] [iOS] eager-read OK: ${file.name} (${arrayBuffer.byteLength} bytes)`
      );
    }

    await commonUploadFile(file, baseContext, props);
  };

  let addrUploadJsx;

  const ErrorUploadIcon = commonUtilityValueReturn(
    isBundle(productName),
    ErrorTriangleIcon,
    ErrorIcon
  );

  const addressProofUploadFail = () => {
    if (uploadStatus === FAILED && !cmAddressDocLoading[fileType]) {
      addrUploadJsx = (
        <Box component="div" sx={classes.statusContainer} tabIndex="0" width="100%">
          <img
            id="uploadAddressProofFail"
            src={ErrorUploadIcon}
            data-testid="upload_failed_id"
            onClick={buttonClickHandler}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                buttonClickHandler(e);
              }
            }}
            alt="Upload Failed"
            height="54px"
            width="54px"
          />
          <Box
            component="span"
            sx={[
              classes.errorText,
              ...(isBundle(productName)
                ? [{ fontSize: '20px', fontWeight: 700, color: '#D5151F', padding: '0 0 10px 0' }]
                : isCDIraproduct(productName)
                ? [{ textDecoration: 'underline' }]
                : []),
            ]}
          >
            {commonTernary(
              isBundle(productName),
              t('Oops! There was an error', {
                defaultValue: 'Oops! There was an error',
              }),
              isIRAEducativaProduct(productName)
                ? t('RelTypeTry', { defaultValue: 'Try Again' })
                : t('Try Again', { defaultValue: 'Try Again' })
            )}
          </Box>
        </Box>
      );
    }
    return addrUploadJsx;
  };
  const acceptFileType = () => {
    const platformId = getCordovaPlatformId();
    const isCordova = platformId === 'ios' || platformId === 'android';
    return isCordova
      ? '*'
      : 'application/pdf,.pdf,image/jpeg,.jpg,.jpeg,.jfif,image/png,.png,image/bmp,.bmp,.bpm,image/x-portable-graymap,.pgm,image/x-portable-pixmap,.ppm,image/gif,.gif,.gifv,image/tiff,.tiff,image/heic,.heic,image/heif,.heif,video/mpeg,.mpg,.mpeg,.hevc';
  };

  useEffect(() => {
    let element = '';
    element = getRoot().getElementById(`${fileType}UploadContainer`);
    if (element) {
      if (isBundle(productName)) {
        if (uploadStatus === DONE && !cmAddressDocLoading[fileType]) {
          const backgroundColor = baseContext.isLightTheme ? '#E7F3E6' : '#0B6C0333';
          element.setAttribute(
            'style',
            `background-image: repeating-linear-gradient(0deg,#76BB70,#76BB70 7px,transparent 8px,transparent 15px),
                        repeating-linear-gradient(90deg,#76BB70,#76BB70 7px,transparent 8px,transparent 15px),
                        repeating-linear-gradient(180deg,#76BB70,#76BB70 7px,transparent 8px,transparent 15px),
                        repeating-linear-gradient(270deg,#76BB70,#76BB70 7px,transparent 8px,transparent 15px) !important;
     background: ${backgroundColor};
     background-size: 1px 100%, 100% 1px, 1px 100%, 100% 1px;
     background-repeat: no-repeat;
     background-position: 0 0, 0 0, 100% 0, 0 100%`
          );
        } else if (uploadStatus === FAILED && !cmAddressDocLoading[fileType]) {
          const backgroundColor = baseContext.isLightTheme ? '#FBE8E9' : '#D5151F1A';
          element.setAttribute(
            'style',
            `background-image: repeating-linear-gradient(0deg,#FF6971,#FF6971 7px,transparent 8px,transparent 15px),repeating-linear-gradient(90deg,#FF6971,#FF6971 7px,transparent 8px,transparent 15px),repeating-linear-gradient(180deg,#FF6971,#FF6971 7px,transparent 8px,transparent 15px),repeating-linear-gradient(270deg,#FF6971,#FF6971 7px,transparent 8px,transparent 15px) !important;  background: ${backgroundColor};background-size: 1px 100%, 100% 1px, 1px 100%, 100% 1px;background-repeat: no-repeat;background-position: 0 0, 0 0, 100% 0, 0 100%`
          );
        }
      } else if (uploadStatus === DONE && !cmAddressDocLoading[fileType]) {
        element.setAttribute(
          'style',
          'background-image: repeating-linear-gradient(0deg,#E37617,#E37617 7px,transparent 8px,transparent 15px),repeating-linear-gradient(90deg,#E37617,#E37617 7px,transparent 8px,transparent 15px),repeating-linear-gradient(180deg,#E37617,#E37617 7px,transparent 8px,transparent 15px),repeating-linear-gradient(270deg,#E37617,#E37617 7px,transparent 8px,transparent 15px) !important; background:#FFFDF2;background-size: 1px 100%, 100% 1px, 1px 100%, 100% 1px;background-repeat: no-repeat;background-position: 0 0, 0 0, 100% 0, 0 100%'
        );
      } else if (uploadStatus === FAILED && !cmAddressDocLoading[fileType]) {
        element.setAttribute(
          'style',
          'background-image: repeating-linear-gradient(0deg,#D71721,#D71721 7px,transparent 8px,transparent 15px),repeating-linear-gradient(90deg,#D71721,#D71721 7px,transparent 8px,transparent 15px),repeating-linear-gradient(180deg,#D71721,#D71721 7px,transparent 8px,transparent 15px),repeating-linear-gradient(270deg,#D71721,#D71721 7px,transparent 8px,transparent 15px) !important; background:#FFF2F2;background-size: 1px 100%, 100% 1px, 1px 100%, 100% 1px;background-repeat: no-repeat;background-position: 0 0, 0 0, 100% 0, 0 100%'
        );
      } else {
        element?.setAttribute(
          'style',
          'background-image: repeating-linear-gradient(0deg,#808285,#808285 7px,transparent 8px,transparent 15px),repeating-linear-gradient(90deg,#808285,#808285 7px,transparent 8px,transparent 15px),repeating-linear-gradient(180deg,#808285,#808285 7px,transparent 8px,transparent 15px),repeating-linear-gradient(270deg,#808285,#808285 7px,transparent 8px,transparent 15px) !important; background:#F4F4F4;background-size: 1px 100%, 100% 1px, 1px 100%, 100% 1px;background-repeat: no-repeat;background-position: 0 0, 0 0, 100% 0, 0 100%'
        );
      }
    }
  }, [uploadStatus, baseContext.isLightTheme]);

  const routeToCameraUpload = () => {
    clearUploadFocus();

    setIsOcrErrMessageFound(false);
    setAddressInformation((prevState) => ({
      ...prevState,
      ocrErrMessage: '',
    }));
    setAddressInformation((prevState) => ({
      ...prevState,
      ocrUIErrors: '',
    }));
    if (baseContext?.wcFlow) {
      history.push({
        pathname: 'camera-upload',
        redirectingPath: pagePath,
        pageProps: props,
        flow: checkFlow,
        fileName: fileType,
      });
    } else {
      history.push({
        pathname: `/${location?.toLowerCase()}/${baseContext.language?.toLowerCase()}/${checkFlow}/${productNameURLMapping(
          productName
        )}/camera-upload`,
        redirectingPath: pagePath,
        pageProps: props,
        flow: checkFlow,
        fileName: fileType,
      });
    }
  };

  const successUploadImg = commonUtilityValueReturn(isBundle(productName), DoneTickGreen, DoneTick);
  return (
    <Grid container>
      {/* <Grid size={{ xs: 12 }}>
        <Typography
          variant="h2"
          data-testid="address-proof"
          className={
            businessFlow
              ? `${classes.top_text_upload_id} ${classes.business_top_text_upload_id}`
              : classes.top_text_upload_id
          }
        >
          {t('Upload Address Proof', { defaultValue: 'Upload Address Proof' })}
          <span style={{ fontSize: '22px', marginLeft: '5px', color: ORIENTAL_ORANGE }}>*</span>
          <span style={{ marginLeft: '10px', opacity: 1 }}>
            <CustomTooltip
              placement="top"
              text={t(`PreviewAddressConsent`, {
                defaultValue: `Address proof is required to open your account if your current home address is not available on your ID. You will need to provide a utility bill with your name and address issued within the last 60 days.`,
              })}
            />
          </span>
        </Typography>
      </Grid> */}

      <input
        type="file"
        data-testid="inp-test"
        accept={acceptFileType()}
        onChange={fileInputHandler}
        ref={fileInput}
        style={{
          opacity: 0,
          position: 'absolute',
          width: 0,
          height: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
      />

      <Grid size={{ xs: 12 }} sx={classes.id_upload_grid}>
        <Box
          component="div"
          style={{
            paddingTop: isBundle(productName) ? '0px' : '20px',
            width: '100%',
            marginBottom: baseContext.flowTypeETB === 'EtbFlow' ? '0px' : '10px',
          }}
          sx={pagePath === 'business-account-info' ? classes.busiAccWidth : ''}
        >
          <Box sx={classes.top_text_upload_id}>
            <>
              <Typography
                component="span"
                variant="h2"
                data-testid="address-proof"
                sx={{
                  ...classes.top_text_upload_id,
                  whiteSpace: 'nowrap',
                  display: 'inline',
                  '@media (max-width: 1160px)': { whiteSpace: 'normal' },
                }}
              >
                {t(uploadDocHeading, { defaultValue: uploadDocHeading })}
                <span style={{ fontSize: '22px', marginLeft: '5px', color: ORIENTAL_ORANGE }}>
                  *
                </span>
              </Typography>
              <Box
                component="span"
                sx={{
                  marginLeft: '10px',
                  opacity: 1,
                  '@media (max-width:400px)': { marginLeft: '0px' },
                }}
              >
                <CustomTooltip
                  placement="top"
                  text={t(toolTipData, {
                    defaultValue: toolTipData,
                  })}
                />
              </Box>
            </>
          </Box>

          <div style={{ marginTop: '5px' }}>
            <Box
              component="div"
              sx={[
                classes.documentUploadContainer,
                classes.uploadContainer,
                classes.box,
                classes.box_a,
                pagePath === 'business-account-info' ? classes.busiAccWidth_box : '',
                isBundle(productName)
                  ? {
                      backgroundColor: theme?.palette?.fieldsBackground?.main,
                      border: `1px solid ${theme?.palette?.fieldsBorder?.main}`,
                      color: theme?.palette?.secondary?.main,
                      '&:focus': {
                        backgroundImage:
                          'repeating-linear-gradient(0deg,blue,blue 7px,transparent 8px,transparent 15px), repeating-linear-gradient(90deg,blue,blue 7px,transparent 8px,transparent 15px), repeating-linear-gradient(180deg,blue,blue 7px,transparent 8px,transparent 15px), repeating-linear-gradient(270deg,blue,blue 7px,transparent 8px,transparent 15px)',
                        borderImage:
                          'repeating-linear-gradient(0deg, blue, blue 1px, transparent 1px, transparent 1px)',
                      },
                    }
                  : {
                      border: '1px dashed #808285',
                      background: '#F4F4F4',
                      '&:focus': {
                        backgroundImage:
                          'repeating-linear-gradient(0deg,blue,blue 7px,transparent 8px,transparent 15px), repeating-linear-gradient(90deg,blue,blue 7px,transparent 8px,transparent 15px), repeating-linear-gradient(180deg,blue,blue 7px,transparent 8px,transparent 15px), repeating-linear-gradient(270deg,blue,blue 7px,transparent 8px,transparent 15px)',
                        borderImage:
                          'repeating-linear-gradient(0deg, blue, blue 1px, transparent 1px, transparent 1px)',
                      },
                    },
              ]}
              id={`${fileType}UploadContainer`}
            >
              {cmAddressDocLoading[fileType] && (
                <Box component="div" sx={classes.errorImgPosition}>
                  <Loader />
                </Box>
              )}

              {uploadStatus === null && !cmAddressDocLoading[fileType] && (
                <Box
                  component="div"
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  sx={{
                    ...classes.uploadSecondaryContainer,
                    flexDirection: columnLayout ? 'column' : 'row',
                    justifyContent: 'space-between',
                    '@media (max-width: 601px)': {
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 2,
                    },
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    component="p"
                    pb={columnLayout ? 3 : 0}
                    sx={{
                      width: '100%',
                      textAlign: isMobile || columnLayout ? 'center' : 'start',
                    }}
                  >
                    {t('Drag and drop the file (or)', {
                      defaultValue: 'Drag and drop the file (or)',
                    })}
                  </Typography>
                  <Box component="div" sx={{ ...classes.btn_grid_containerira, width: '100%' }}>
                    <Button
                      disableFocusRipple
                      color="inherit"
                      id="uploadAddressProofInitial"
                      data-testid="null_testid"
                      onClick={buttonClickHandler}
                      sx={[
                        classes.docBtnStyles,
                        {
                          border: `1.6px solid ${theme?.palette?.orientalPrimary?.main}`,
                          color: theme?.palette?.orientalPrimary?.main,
                        },
                      ]}
                    >
                      {t('Upload Now', { defaultValue: 'Upload Now' })}
                    </Button>
                    <Button
                      disableFocusRipple
                      color="inherit"
                      variant="outlined"
                      sx={[
                        { ...classes.docBtnStyles, maxWidth: '107px' },
                        {
                          border: `1.6px solid ${theme?.palette?.orientalPrimary?.main}`,
                          color: theme?.palette?.orientalPrimary?.main,
                        },
                      ]}
                      onClick={routeToCameraUpload}
                      data-testid="camera_testid"
                    >
                      {t('Camera', { defaultValue: 'Camera' })}
                    </Button>
                  </Box>
                </Box>
              )}
              {uploadStatus === DONE && !cmAddressDocLoading[fileType] && (
                <Box component="div" sx={classes.statusContainer}>
                  <img src={successUploadImg} height="54px" width="54px" alt="Upload successful" />
                  <Box
                    component="span"
                    sx={isBundle(productName) ? classes.doneTextBundle : classes.doneText}
                  >
                    {isBundle(productName)
                      ? t('Done!', { defaultValue: 'Done!' })
                      : isIRAEducativaProduct(productName)
                      ? t('RelTypeDone', { defaultValue: 'Done' })
                      : t('Done', { defaultValue: 'Done' })}
                  </Box>
                </Box>
              )}

              {addressProofUploadFail()}
            </Box>
            <Box component="div" sx={classes.supportedText}>
              <span
                style={{
                  paddingRight: '10px',
                  color:
                    isBundle(productName) &&
                    uploadStatus === FAILED &&
                    !cmAddressDocLoading[fileType]
                      ? '#FF6971'
                      : '#727578',
                }}
              >
                {t('Supported file types: PNG, JPG, PDF.', {
                  defaultValue: 'Supported file types: PNG, JPG, PDF.',
                  nsSeparator: '|',
                })}
              </span>
              <span
                style={{
                  paddingLeft: '10px',
                  color:
                    isBundle(productName) &&
                    uploadStatus === FAILED &&
                    !cmAddressDocLoading[fileType]
                      ? '#FF6971'
                      : '#727578',
                }}
              >
                {t('Maximum: 10MB', {
                  defaultValue: 'Maximum: 10MB',
                  nsSeparator: '|',
                })}
              </span>
            </Box>

            {uploadStatus === FAILED && !cmAddressDocLoading[fileType] && !isBundle(productName) && (
              <Box
                component="span"
                id="addressUploadId"
                sx={
                  isOcrErrMessageFound ||
                  (!addressInformation.ocrAddressVerified && productMapping(productName))
                    ? classes.ocrErrorStyle
                    : classes.errorStyle
                }
              >
                {isOcrErrMessageFound
                  ? t(addressInformation.ocrErrorMessage, {
                      defaultValue: addressInformation.ocrErrorMessage,
                      nsSeparator: '|',
                    })
                  : t(uploadError.ocrUIErrors, {
                      defaultValue: uploadError.ocrUIErrors,
                      nsSeparator: '|',
                    })}{' '}
                {uploadError.ocrUIErrors?.includes('Only files with the following formats') && (
                  <Box component="div" sx={classes.errorStyle}>
                    <Box
                      component="span"
                      role="button"
                      sx={{ fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}
                      onClick={buttonClickHandler}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          buttonClickHandler(e);
                        }
                      }}
                      tabIndex={0}
                    >
                      {t('Click to upload again.', { defaultValue: 'Click to upload again.' })}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </div>
        </Box>
      </Grid>

      {baseContext?.taxUploadProgress[fileType] === 100 &&
        baseContext?.taxLinearProgress[fileType] &&
        uploadData?.document && (
          <Grid container spacing={2} my={2} sx={bundleClasses.uploadDone}>
            <Grid size={{ md: 1 }} sx={bundleClasses.verificationPending}>
              <FileIcon
                width="16px"
                height="21px"
                color={
                  uploadStatus === 'FAILED'
                    ? '#D5151F'
                    : uploadStatus === 'DONE' && !isLightTheme
                    ? '#FFFFFF'
                    : theme?.palette?.subtext?.main
                }
              />
            </Grid>
            <Grid size={{ md: 10 }}>
              <Typography component="h5" paddingY="5px" sx={bundleClasses.fileUploadName}>
                {uploadFileData?.name ? uploadFileData?.name : fileType}
              </Typography>
              {uploadFileData?.size && (
                <Typography component="p" sx={bundleClasses.fileUploadSize}>
                  {Math.floor(uploadFileData.size / 1024)} KB
                </Typography>
              )}
            </Grid>

            <Grid size={{ md: 1 }}>
              {baseContext?.taxUploadProgress[fileType] === 100 &&
                baseContext?.taxLinearProgress[fileType] && (
                  //   <GreenTickFill height="20px" width="20px" />
                  <IconButton
                    id="closeIconId"
                    name={uploadFileData?.name}
                    data-testid={`cancel_testid_${uploadFileData?.name}`}
                    aria-label="delete"
                    onClick={() => {
                      setUploadStatus(null);
                      setTaxUploadProgress((prev) => ({ ...prev, [fileType]: 0 }));
                      setTaxLinearProgress((prev) => ({ ...prev, [fileType]: false }));
                      setUploadData((prevState) => ({
                        ...prevState,
                        document: '',
                      }));
                      setUploadDataError((prevState) => ({
                        ...prevState,
                        document: '',
                      }));
                    }}
                    // onKeyDown={removeUploadedFile}
                    size="small"
                    color="inherit"
                  >
                    <DeleteIcon
                      color={
                        uploadStatus === 'FAILED'
                          ? '#D5151F'
                          : uploadStatus === 'DONE' && !isLightTheme
                          ? '#FFFFFF'
                          : theme?.palette?.subtext?.main
                      }
                    />
                  </IconButton>
                )}
            </Grid>
          </Grid>
        )}
    </Grid>
  );
}

CommonDocumentUpload.propTypes = {
  pagePath: PropTypes.string,
  uploadDocHeading: PropTypes.string,
  toolTipData: PropTypes.string,
  uploadStatus: PropTypes.string,
  vouchedAddrEdited: PropTypes.bool,
  isBundleFlag: PropTypes.bool,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  handleChange: PropTypes.func,
  setUploadData: PropTypes.func,
  setUploadStatus: PropTypes.func,
  setUploadDataError: PropTypes.func,
  productDataIRAErrors: PropTypes.object,
  uploadError: PropTypes.object,
  uploadFileData: PropTypes.object,
  setUploadFileData: PropTypes.func,
  fileType: PropTypes.string,
  uploadData: PropTypes.string,
  columnLayout: PropTypes.bool,
};

export default withRouter(CommonDocumentUpload);
